import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useEffect } from 'react';
import { WashingMachine, CheckCheck, Eye, ListChecks } from 'lucide-react';
import api, { apiError } from '../lib/api';
import { formatDate, formatSizes, PAY_STATUS } from '../lib/format';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

export default function Laundry() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [confirmFor, setConfirmFor] = useState(null);
  const [detailFor, setDetailFor] = useState(null);
  const [partialFor, setPartialFor] = useState(null);
  const [picked, setPicked] = useState([]);

  const load = () =>
    api.get('/monitoring').then((r) => setItems(r.data.laundry || [])).catch((err) => {
      toast.error(apiError(err, 'Gagal memuat daftar laundry'));
      setItems([]);
    });
  useEffect(() => {
    load();
  }, []);

  const markDone = async () => {
    try {
      await api.post(`/transactions/${confirmFor.id}/laundry-done`);
      toast.success(`Laundry selesai — stok ${confirmFor.transaction_number} ready kembali`);
      setConfirmFor(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const openPartial = (tx) => {
    setPicked({});
    setPartialFor(tx);
  };

  const toggleSize = (i, s, q) =>
    setPicked((cur) => {
      const row = { sizes: {}, bawahan: false, ...(cur[i] || {}) };
      const sizes = { ...row.sizes };
      if (sizes[s]) delete sizes[s]; else sizes[s] = q;
      const next = { ...cur, [i]: { ...row, sizes } };
      if (!Object.keys(sizes).length && !row.bawahan) delete next[i];
      return next;
    });

  const toggleBawahan = (i) =>
    setPicked((cur) => {
      const row = { sizes: {}, ...(cur[i] || {}) };
      const next = { ...cur, [i]: { ...row, bawahan: !row.bawahan } };
      if (!Object.keys(row.sizes || {}).length && row.bawahan) delete next[i];
      return next;
    });

  const pickedCount = Object.values(picked).reduce(
    (n, r) => n + Object.keys(r.sizes || {}).length + (r.bawahan ? 1 : 0), 0
  );

  const submitPartial = async () => {
    try {
      const payload = {
        rows: Object.entries(picked).map(([i, v]) => ({ index: Number(i), sizes: v.sizes, bawahan: v.bawahan })),
      };
      const r = await api.post(`/transactions/${partialFor.id}/laundry-done-rows`, payload);
      const allDone = r.data.rows.every((x) => x.laundry_done);
      toast.success(allDone ? 'Semua barang selesai laundry — stok ready kembali' : 'Item terpilih selesai laundry — stoknya ready kembali');
      setPartialFor(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const rowLeftText = (r) => {
    const done = r.laundry_done_sizes || {};
    const parts = Object.entries(r.sizes || {})
      .map(([s, q]) => [s, q - (done[s] || 0)])
      .filter(([, q]) => q > 0);
    const pcs = parts.reduce((n, [, q]) => n + q, 0);
    const sizeTxt = parts.map(([s, q]) => `${s}:${q}`).join(' ');
    const bw = r.bawahan_name && !r.bawahan_laundry_done ? ` + ${r.bawahan_name}` : '';
    return pcs > 0 || bw ? `${r.product_name} — ${pcs} pcs${sizeTxt ? ` (${sizeTxt})` : ''}${bw}` : null;
  };

  if (!items) return <p className="text-sm text-mocha py-16 text-center">Memuat daftar laundry…</p>;

  const pendingRows = (tx) => tx.rows.filter((r) => !r.laundry_done);

  return (
    <div className="space-y-6" data-testid="laundry-page">
      <div className="flex items-center gap-3">
        <span className="w-11 h-11 rounded-2xl bg-orange-100 text-orange-700 flex items-center justify-center">
          <WashingMachine size={22} />
        </span>
        <div>
          <h1 className="font-display text-3xl text-ink tracking-tight">Laundry</h1>
          <p className="text-sm text-mocha mt-0.5">
            {items.length} order sedang di laundry. &quot;Selesai Sebagian&quot; untuk percepatan item tertentu.
          </p>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="bg-white rounded-2xl border border-border shadow-soft p-10 text-center" data-testid="laundry-empty">
          <WashingMachine size={32} className="mx-auto text-mocha/40" />
          <p className="font-display text-lg text-ink mt-3">Tidak ada barang di laundry</p>
          <p className="text-xs text-mocha mt-1">Semua stok siap disewakan.</p>
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-3 items-start">
          {items.map((tx) => {
            const ps = PAY_STATUS[tx.payment_status];
            const pending = pendingRows(tx);
            return (
              <div
                key={tx.id}
                data-testid={`laundry-card-${tx.transaction_number}`}
                className="bg-white rounded-2xl border border-border shadow-soft p-4"
              >
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <button
                        onClick={() => navigate(`/transaksi/${tx.id}`)}
                        data-testid={`laundry-open-${tx.transaction_number}`}
                        className="font-display font-bold text-brown hover:underline"
                      >
                        {tx.transaction_number}
                      </button>
                      <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold ${ps?.cls}`}>{ps?.label}</span>
                    </div>
                    <p className="text-sm font-semibold text-ink mt-1">{tx.customer_name}</p>
                    <p className="text-xs text-mocha mt-0.5">
                      Kembali {formatDate(tx.return_date)} • {tx.ready_date >= '2099' ? 'Tanpa batas tanggal' : `Estimasi ready ${formatDate(tx.ready_date)}`}
                    </p>
                    <p className="text-xs text-mocha mt-0.5" data-testid={`laundry-items-${tx.transaction_number}`}>
                      {pending.map((r) => rowLeftText(r)).filter(Boolean).join(', ')}
                      {pending.length < tx.rows.length && (
                        <span className="text-success font-semibold"> • {tx.rows.length - pending.length} item sudah selesai</span>
                      )}
                    </p>
                  </div>
                  <div className="flex gap-1.5 shrink-0">
                    <button
                      data-testid={`laundry-view-${tx.transaction_number}`}
                      onClick={() => setDetailFor(tx)}
                      className="p-2.5 rounded-xl bg-brown/10 text-brown"
                      title="Lihat daftar barang"
                    >
                      <Eye size={15} />
                    </button>
                    {tx.rows.length > 1 && pending.length > 1 && (
                      <button
                        data-testid={`laundry-partial-${tx.transaction_number}`}
                        onClick={() => openPartial(tx)}
                        className="inline-flex items-center gap-1.5 rounded-xl bg-orange-600 text-white px-3 py-2 text-xs font-bold hover:bg-orange-700"
                      >
                        <ListChecks size={13} /> Selesai Sebagian
                      </button>
                    )}
                    <button
                      data-testid={`laundry-done-${tx.transaction_number}`}
                      onClick={() => setConfirmFor(tx)}
                      className="inline-flex items-center gap-1.5 rounded-xl bg-success text-white px-3 py-2 text-xs font-bold hover:bg-success/90"
                    >
                      <CheckCheck size={13} /> Selesai Laundry
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Sheet open={!!detailFor} onOpenChange={() => setDetailFor(null)}>
        <SheetContent side="bottom" className="rounded-t-3xl bg-ivory max-h-[70vh] overflow-y-auto" data-testid="laundry-detail-sheet">
          <SheetHeader>
            <SheetTitle className="font-display text-brown-dark">
              Barang di Laundry — {detailFor?.transaction_number}
            </SheetTitle>
          </SheetHeader>
          <div className="py-4 space-y-2">
            <p className="text-xs text-mocha">{detailFor?.customer_name} • kembali {formatDate(detailFor?.return_date)}</p>
            {detailFor?.rows.map((r, i) => (
              <div key={i} data-testid={`laundry-detail-row-${i}`} className={`bg-white rounded-xl border px-4 py-3 flex justify-between items-center ${r.laundry_done ? 'border-success/40 opacity-60' : 'border-border'}`}>
                <div>
                  <p className="text-sm font-semibold text-ink">{r.product_name}</p>
                  <p className="text-xs text-mocha">
                    {Object.entries(r.sizes || {}).map(([s, q]) => {
                      const d = (r.laundry_done_sizes || {})[s] || 0;
                      return `${s}:${q}${d ? ` (✓${d} selesai)` : ''}`;
                    }).join(', ')}
                    {r.bawahan_name ? ` + ${r.bawahan_name}${r.bawahan_laundry_done ? ' (✓ selesai)' : ''}` : ''}
                  </p>
                </div>
                <span className={`text-xs font-bold ${r.laundry_done ? 'text-success' : 'text-orange-700'}`}>
                  {r.qty} pcs{r.laundry_done ? ' • selesai' : ''}
                </span>
              </div>
            ))}
          </div>
        </SheetContent>
      </Sheet>

      <Dialog open={!!partialFor} onOpenChange={() => setPartialFor(null)}>
        <DialogContent className="bg-ivory max-w-md" data-testid="laundry-partial-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-brown-dark">Selesai Sebagian — {partialFor?.transaction_number}</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-mocha">Centang barang yang sudah selesai dicuci (percepatan). Stok item itu langsung ready lagi; sisanya tetap di laundry.</p>
          <div className="space-y-2.5 mt-2">
            {partialFor?.rows.map((r, i) => {
              const done = r.laundry_done_sizes || {};
              if (r.laundry_done) {
                return (
                  <div key={i} className="bg-white rounded-xl border border-success/40 p-3 opacity-50">
                    <p className="text-sm font-semibold text-ink">{r.product_name} <span className="text-[10px] font-bold text-success">sudah selesai</span></p>
                  </div>
                );
              }
              return (
                <div key={i} data-testid={`partial-row-${i}`} className="bg-white rounded-xl border border-border p-3 space-y-2">
                  <p className="text-sm font-semibold text-ink">{r.product_name}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {Object.entries(r.sizes || {}).map(([s, q]) => {
                      const left = q - (done[s] || 0);
                      if (left <= 0) return (
                        <span key={s} className="rounded-full px-3 py-1.5 text-xs font-semibold bg-success/10 text-success line-through">{s} — {q} pcs ✓</span>
                      );
                      const on = !!picked[i]?.sizes?.[s];
                      return (
                        <label key={s} data-testid={`partial-size-${i}-${s.replace(/[^A-Za-z0-9]+/g, '-')}`}
                          className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold cursor-pointer border transition-colors ${on ? 'bg-brown text-white border-brown' : 'bg-ivory border-border text-mocha'}`}>
                          <input type="checkbox" className="hidden" checked={on} onChange={() => toggleSize(i, s, left)} />
                          {s} — {left} pcs
                        </label>
                      );
                    })}
                    {r.bawahan_name && !r.bawahan_laundry_done && (
                      <label data-testid={`partial-bawahan-${i}`}
                        className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold cursor-pointer border transition-colors ${picked[i]?.bawahan ? 'bg-orange-600 text-white border-orange-600' : 'bg-ivory border-border text-mocha'}`}>
                        <input type="checkbox" className="hidden" checked={!!picked[i]?.bawahan} onChange={() => toggleBawahan(i)} />
                        {r.bawahan_name} (bawahan)
                      </label>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <button
            data-testid="partial-submit"
            onClick={submitPartial}
            disabled={pickedCount === 0}
            className="mt-3 w-full rounded-xl bg-orange-600 text-white py-3 text-sm font-bold hover:bg-orange-700 disabled:opacity-50"
          >
            Tandai {pickedCount} Item Selesai Laundry
          </button>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmFor} onOpenChange={() => setConfirmFor(null)}>
        <AlertDialogContent data-testid="laundry-done-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-brown-dark">Selesai Laundry?</AlertDialogTitle>
            <AlertDialogDescription>
              Semua barang pada order {confirmFor?.transaction_number} ({confirmFor?.customer_name}) sudah selesai
              dicuci. Stok akan langsung kembali <b>Ready</b> dan bisa disewa lagi mulai hari ini.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="laundry-done-cancel">Batal</AlertDialogCancel>
            <AlertDialogAction data-testid="laundry-done-confirm" onClick={markDone} className="bg-success hover:bg-success/90">
              Ya, Selesai Laundry
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
